import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { PeriodFields } from '@/components/internal-staff/PeriodFields';
import { TimeEntryList } from '@/components/internal-staff/TimeEntryList';
import { apiError, initialPeriod } from '@/components/internal-staff/shared';
import { useInternalRefresh } from '@/components/internal-staff/useInternalRefresh';
import { GeolocationSwitch } from '@/components/internal-staff/GeolocationSwitch';
import { RefreshStatus } from '@/components/internal-staff/RefreshStatus';
import { notifyInternalChange } from '@/components/internal-staff/useInternalRefresh';
import { canOpenGeoDemo } from '@/components/internal-staff/geo-demo/access';

export default function AdminInternalHours({ user }) {
  const [period, setPeriod] = useState(initialPeriod);
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [error, setError] = useState('');
  const [readError, setReadError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const revision = useInternalRefresh();
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/internal-staff/admin/overview`, { withCredentials: true, timeout: 15000, params: { start_date: period.start, end_date: period.end } })
      .then(res => { if (!cancelled) { setEntries(res.data.entries); setLoaded(true); setReadError(''); setUpdatedAt(Date.now()); } })
      .catch(err => { if (!cancelled) setReadError(apiError(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period.start, period.end, refresh, revision]);
  const review = async (entry, status, reason) => {
    setBusy(true); setError('');
    try {
      const res = await axios.put(`${API}/internal-staff/admin/entries/${entry.entry_id}/review`, { status, reason }, { withCredentials: true });
      setEntries(previous => previous.map(row => row.entry_id === entry.entry_id ? res.data : row));
    } catch (err) { setError(apiError(err)); }
    finally { setBusy(false); }
  };
  const correct = async (entry, payload) => {
    setBusy(true); setError('');
    try {
      const res = await axios.put(`${API}/internal-staff/admin/entries/${entry.entry_id}/correct`, payload, { withCredentials: true });
      setEntries(previous => previous.map(row => row.entry_id === entry.entry_id ? res.data : row));
    } finally { setBusy(false); }
  };
  const reopen = async (entry, reason) => {
    setBusy(true); setError('');
    try {
      const res = await axios.put(`${API}/internal-staff/admin/entries/${entry.entry_id}/reopen`, { reason }, { withCredentials: true });
      setEntries(previous => previous.map(row => row.entry_id === entry.entry_id ? res.data : row));
      notifyInternalChange(); return res.data;
    } catch (err) { throw err; }
    finally { setBusy(false); }
  };
  const cancel = async (entry, reason) => {
    setBusy(true); setError('');
    try {
      const res = await axios.put(`${API}/internal-staff/admin/entries/${entry.entry_id}/cancel`, { reason }, { withCredentials: true });
      setEntries(previous => previous.map(row => row.entry_id === entry.entry_id ? res.data : row));
    } catch (err) { setError(apiError(err)); }
    finally { setBusy(false); }
  };
  const visible = entries.filter(e => filter === 'all' || (filter === 'open' ? !e.ended_at : e.status === filter));
  return <Layout user={user} activeModule="b2b">
    <div className="max-w-5xl mx-auto space-y-6" data-testid="admin-internal-hours-page">
      <Link to="/admin/b2b/planning" className="text-sm underline" data-testid="internal-hours-back">Retour au planning</Link>
      <h1 className="text-3xl font-bold" data-testid="internal-hours-title">Horaires des salariés internes</h1>
      <p className="text-sm text-slate-600 border-l-2 border-slate-400 pl-3" data-testid="internal-review-rules">Seules les heures validées sont retenues pour le salaire. Une prestation par journée de service ; seuls les responsables corrigent ou rouvrent une prestation (motif obligatoire, valeurs avant/après tracées, dispatcher informé). Une prestation oubliée reste ouverte et est signalée : aucune clôture automatique.</p>
      <GeolocationSwitch user={user} />
      {process.env.NODE_ENV === 'development' && canOpenGeoDemo(user) && <a href="/admin/b2b/geolocation-acceptance" className="inline-flex text-sm font-medium underline underline-offset-4 text-emerald-900" data-testid="geo-acceptance-open">Recette fonctionnelle · contrôle 500 m et exceptions persistantes</a>}
      {(user.is_super_admin || user.operations_manager) && <Link to="/operations/location-exceptions" className="inline-flex text-sm underline" data-testid="geo-control-review-open">Exceptions de lieu</Link>}
      {/* Nouvelle page complète pour abandonner aussi les lectures encore en vol de l'écran métier précédent. */}
      {process.env.NODE_ENV === 'development' && canOpenGeoDemo(user) && <a href="/admin/b2b/geolocation-simulation" className="inline-flex text-sm underline underline-offset-4 text-slate-600 hover:text-slate-900 transition-colors" data-testid="geo-demo-open">Maquette localisation et exceptions · Simulation — règle non activée</a>}
      <div className="flex flex-wrap items-end gap-4"><PeriodFields period={period} onChange={setPeriod} prefix="internal-review" />
        <label className="text-sm">Validation<select className="block border bg-white rounded-md p-2" value={filter} onChange={e => setFilter(e.target.value)} data-testid="internal-review-filter"><option value="pending">En attente</option><option value="open">Non clôturées</option><option value="correction_requested">À corriger</option><option value="approved">Validé</option><option value="rejected">Refusé</option><option value="cancelled">Annulées</option><option value="all">Tous</option></select></label>
        <Button variant="outline" disabled={loading} onClick={() => setRefresh(v => v + 1)} data-testid="internal-review-refresh">Actualiser</Button>
      </div>
      {error && <p role="alert" className="text-red-700" data-testid="internal-review-error">{error}</p>}
      {loading && !loaded && <p data-testid="internal-review-loading">Chargement…</p>}
      <RefreshStatus loading={loading} error={readError} updatedAt={updatedAt} prefix="internal-review" />
      {loaded && <TimeEntryList entries={visible} onReview={review} onCorrect={correct} onReopen={reopen} onCancel={cancel} busy={busy} />}
    </div>
  </Layout>;
}